// The one place the frontend knows currency symbols and display decimals.
// Money values are decimal major units (dong, dollars) end to end; the server
// serializes them as JSON numbers (see config/initializers/json_big_decimal.rb).
// Mirrors OrgNode::CURRENCY_DISPLAY — parity-checked by spec.

export interface CurrencyDisplay {
  symbol: string
  decimals: number
}

export const CURRENCY_DISPLAY: Record<string, CurrencyDisplay> = {
  VND: { symbol: '₫', decimals: 0 },
  USD: { symbol: '$', decimals: 2 },
  SGD: { symbol: 'S$', decimals: 2 },
  THB: { symbol: '฿', decimals: 2 },
  PHP: { symbol: '₱', decimals: 2 },
  IDR: { symbol: 'Rp', decimals: 0 },
  MYR: { symbol: 'RM', decimals: 2 },
  CNY: { symbol: '¥', decimals: 2 },
  JPY: { symbol: '¥', decimals: 0 },
  KRW: { symbol: '₩', decimals: 0 },
  EUR: { symbol: '€', decimals: 2 },
  GBP: { symbol: '£', decimals: 2 },
}

const FALLBACK: CurrencyDisplay = CURRENCY_DISPLAY.VND

export function currencyDisplay(currency?: string | null): CurrencyDisplay {
  return (currency && CURRENCY_DISPLAY[currency]) || FALLBACK
}

export function toNumber(value: string | number): number {
  return typeof value === 'string' ? Number(value) : value
}

function trim(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '')
}

/**
 * Compact money: symbol + T/B/M/K with one decimal, trailing .0 trimmed.
 * formatMoney(1_530_000_000) → "₫1.5B"; formatMoney(19.99, 'USD') → "$19.99".
 */
export function formatMoney(value: string | number, currency?: string | null): string {
  const n = toNumber(value)
  const { symbol, decimals } = currencyDisplay(currency)
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return `${symbol}0`
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000_000_000) return `${sign}${symbol}${trim(abs / 1_000_000_000_000)}T`
  if (abs >= 1_000_000_000) return `${sign}${symbol}${trim(abs / 1_000_000_000)}B`
  if (abs >= 1_000_000) return `${sign}${symbol}${trim(abs / 1_000_000)}M`
  if (abs >= 1_000) return `${sign}${symbol}${trim(abs / 1_000)}K`
  return `${sign}${symbol}${decimals > 0 ? abs.toFixed(decimals) : String(abs)}`
}

/**
 * Historical compact variant used by Bottom Up / Wholesale grids: T/B keep one
 * fixed decimal ("₫2.0B"), M/K round to whole ("₫1M"). Kept pixel-identical
 * for VND; currency-aware for symbol and sub-1000 decimals.
 */
export function formatMoneyShort(value: string | number, currency?: string | null): string {
  const n = toNumber(value)
  const { symbol, decimals } = currencyDisplay(currency)
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return `${symbol}0`
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000_000).toFixed(1)}T`
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(0)}M`
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(0)}K`
  return `${sign}${symbol}${decimals > 0 ? abs.toFixed(decimals) : String(abs)}`
}

/**
 * formatMoneyShort without the currency symbol — for dense grids where the
 * currency is unambiguous from context (e.g. actual/plan pairs). Sub-1000
 * values round to whole numbers: cents would overflow the pair and exact
 * amounts live in tooltips.
 */
export function formatMoneyShortBare(value: string | number, currency?: string | null): string {
  const n = toNumber(value)
  if (Number.isFinite(n) && Math.abs(n) < 1000) return String(Math.round(n))
  const { symbol } = currencyDisplay(currency)
  return formatMoneyShort(value, currency).replace(symbol, '')
}

/**
 * Full (non-compact) number, no symbol — used in editing contexts and
 * tooltips. VND: whole numbers with commas (unchanged); exponent-2
 * currencies show cents.
 */
export function formatMoneyFull(value: string | number, currency?: string | null): string {
  const n = toNumber(value)
  if (!Number.isFinite(n)) return String(value)
  const { decimals } = currencyDisplay(currency)
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(n)
}

/**
 * Parses shorthand money strings into major units.
 * "120M" → 120_000_000; "1.53B" → 1_530_000_000; "12.99" → 12.99 (USD).
 * Strips commas and any known currency symbol. Rounds to the currency's
 * display decimals (whole dong for VND, cents for USD).
 */
export function parseMoney(input: string, currency?: string | null): number | null {
  const { decimals } = currencyDisplay(currency)
  let trimmed = input.trim().replace(/,/g, '')
  for (const { symbol } of Object.values(CURRENCY_DISPLAY)) {
    trimmed = trimmed.split(symbol).join('')
  }
  trimmed = trimmed.trim()
  if (!trimmed) return null

  const match = trimmed.match(/^(-?[0-9]*\.?[0-9]+)\s*([kmbtKMBT])?$/)
  if (!match) return null

  const num = parseFloat(match[1])
  if (isNaN(num)) return null

  const multipliers: Record<string, number> = {
    '': 1,
    K: 1_000,
    M: 1_000_000,
    B: 1_000_000_000,
    T: 1_000_000_000_000,
  }
  const raw = num * (multipliers[(match[2] || '').toUpperCase()] || 1)
  const scale = 10 ** decimals
  return Math.round(raw * scale) / scale
}
